import type { Action } from 'cadenza/action';
import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';
import type { Authority } from 'cadenza/api-client/srs-api';
import type { IndividualValuesTableDataExportOptions } from 'cadenza/api-client/workbook-export-data-api';
import { isFeatureAvailable } from 'cadenza/features';
import { getStore } from 'cadenza/store/cadenza-store';
import type { Objecttype } from 'cadenza/workbook/api/objecttype';
import { ExportIndividualValuesDrillThroughToCsvAction } from 'cadenza/workbook/individual-values-table-dialog/export-individual-values-drill-through-to-csv-action';
import { ExportIndividualValuesDrillThroughToXlsxAction } from 'cadenza/workbook/individual-values-table-dialog/export-individual-values-drill-through-to-xlsx-action';
import { ExportGpxAction } from 'cadenza/workbook/map/actions/export-gpx-action';
import { ExportKmlAction } from 'cadenza/workbook/map/actions/export-kml-action';
import { ExportShapefileAction } from 'cadenza/workbook/map/actions/export-shapefile-action';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { isAttributeToOneReachableFrom } from 'cadenza/workbook/state/workbook-reachability.state';
import { ExportGeopackageViewToolbarAction } from 'cadenza/workbook/view/view-header/actions/export-geopackage-view-toolbar-action';
import {
  getAttributeById,
  getDataViewById,
  getDataViewIdsByViewId,
  getObjecttypesByDataViewId,
  getViewByDataViewId,
  getWorkbookNameAndHash,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import { ExportGeopackageAction } from 'cadenza/workbook/workbook-panel/settings/actions/export-geopackage-action';
import { ExportToCsvAction } from 'cadenza/workbook/workbook-panel/settings/table/export-to-csv-action';
import { ExportToXlsxAction } from 'cadenza/workbook/workbook-panel/settings/table/export-to-xlsx-action';
import { TableExportGeoPackageAction } from 'cadenza/workbook/workbook-panel/settings/table/table-export-geo-package-action';

export type ExportActionContext =
  | { type: 'TOOLBAR' | 'SELECTION_CART' | 'LAYER_LIST' }
  | { type: 'INDIVIDUAL_VALUES_DRILL_THROUGH'; paramsProvider: () => IndividualValuesTableDataExportOptions }
  | { type: 'ATTRIBUTE_TABLE'; srs: Authority; extent: WorldBoxDto };

type ExportAction = Action & { mediaType: string };

export function getActiveExportActions(dataViewId: DataViewId, actionContext: ExportActionContext): ExportAction[] {
  const result: ExportAction[] = [];
  const view = getViewByDataViewId(getStore().getState(), dataViewId);
  const viewType = actionContext.type === 'ATTRIBUTE_TABLE' ? 'TABLE' : view.type;
  const objecttypes = getObjecttypesByDataViewId(getStore().getState(), dataViewId);
  const workbookExportDataAndImagesFeatureAvailable = isWorkbookFeatureAvailable(
    getStore().getState(),
    'workbook-view-data-and-images-export',
  );
  const dataExportAllowed = isDataExportAllowed(objecttypes) && workbookExportDataAndImagesFeatureAvailable;
  const geoExportAllowed = isGeoExportAllowed(objecttypes) && workbookExportDataAndImagesFeatureAvailable;

  if (
    geoExportAllowed &&
    isFeatureAvailable('KML_EXPORT') &&
    viewType === 'MAP' &&
    (actionContext.type === 'SELECTION_CART' || actionContext.type === 'LAYER_LIST')
  ) {
    result.push(new ExportKmlAction(dataViewId, actionContext));
  }

  if (
    geoExportAllowed &&
    isFeatureAvailable('SHAPEFILE_EXPORT') &&
    viewType === 'MAP' &&
    (actionContext.type === 'SELECTION_CART' || actionContext.type === 'LAYER_LIST')
  ) {
    result.push(new ExportShapefileAction(dataViewId, actionContext));
  }

  if (dataExportAllowed && isFeatureAvailable('EXCEL_EXPORT') && viewType === 'TABLE') {
    if (actionContext.type !== 'INDIVIDUAL_VALUES_DRILL_THROUGH') {
      result.push(new ExportToXlsxAction(dataViewId, actionContext));
    } else {
      result.push(
        new ExportIndividualValuesDrillThroughToXlsxAction(
          getWorkbookNameAndHash(getStore().getState()),
          view.id,
          actionContext.paramsProvider,
        ),
      );
    }
  }

  if (dataExportAllowed && isFeatureAvailable('CSV_EXPORT') && viewType === 'TABLE') {
    if (actionContext.type !== 'INDIVIDUAL_VALUES_DRILL_THROUGH') {
      result.push(new ExportToCsvAction(dataViewId, actionContext));
    } else {
      result.push(
        new ExportIndividualValuesDrillThroughToCsvAction(
          getWorkbookNameAndHash(getStore().getState()),
          view.id,
          actionContext.paramsProvider,
        ),
      );
    }
  }

  if (isFeatureAvailable('EXPORT_GEOPACKAGE')) {
    if (
      geoExportAllowed &&
      viewType === 'MAP' &&
      (actionContext.type === 'SELECTION_CART' || actionContext.type === 'LAYER_LIST')
    ) {
      result.push(new ExportGeopackageAction(dataViewId, actionContext));
    } else if (workbookExportDataAndImagesFeatureAvailable && viewType === 'MAP' && actionContext.type === 'TOOLBAR') {
      // For view-toolbar, we need to look for all dataViews in a view
      const allDataViewIds = getDataViewIdsByViewId(
        getStore().getState(),
        getViewByDataViewId(getStore().getState(), dataViewId).id,
      );
      const geoExportAllowedInAnyDataView = allDataViewIds.some((id) =>
        isGeoExportAllowed(getObjecttypesByDataViewId(getStore().getState(), id)),
      );
      result.push(new ExportGeopackageViewToolbarAction(dataViewId, actionContext, geoExportAllowedInAnyDataView));
    } else if (dataExportAllowed && viewType === 'TABLE') {
      result.push(
        new TableExportGeoPackageAction(
          dataViewId,
          actionContext,
          actionContext.type === 'INDIVIDUAL_VALUES_DRILL_THROUGH',
        ),
      );
    }
  }
  if (
    isFeatureAvailable('GPX_EXPORT') &&
    geoExportAllowed &&
    viewType === 'MAP' &&
    actionContext.type === 'LAYER_LIST'
  ) {
    const dataView = getDataViewById(getStore().getState(), dataViewId);
    if (dataView.visualizationKey === 'MAP.ROUTES') {
      const exportGpxAction = new ExportGpxAction(dataViewId, actionContext);
      result.push(exportGpxAction);
    } else {
      const visualizeAsSinglePoint = dataView.visualizationKey === 'MAP.POINT';
      const attributes = Object.values(dataView.attributes)
        .flat()
        .map((a) => getAttributeById(getStore().getState(), a));
      const hasSinglePointGeometryAttribute = attributes.find(
        (wa) =>
          (wa.type === 'GEOMETRY' && wa.simpleGeometryType === 'POINT') ||
          (wa.hasGeometryRelation && wa.relatedGeometryType === 'POINT'),
      );
      if (visualizeAsSinglePoint && hasSinglePointGeometryAttribute) {
        const toOneDateAttribute = attributes.find(
          (wa) =>
            wa.type === 'DATE' &&
            isAttributeToOneReachableFrom(getStore().getState(), hasSinglePointGeometryAttribute, wa),
        );
        result.push(new ExportGpxAction(dataViewId, actionContext, !!toOneDateAttribute));
      }
    }
  }

  return result;
}

function isDataExportAllowed(objecttypes: Objecttype[]) {
  return objecttypes.length && objecttypes.every((ot) => ot.properties?.['allow-data-export'] === 'true');
}

function isGeoExportAllowed(objecttypes: Objecttype[]) {
  return (
    objecttypes.length &&
    objecttypes.every(
      (ot) => ot.properties?.['allow-data-export'] === 'true' && ot.properties?.['allow-geo-export'] === 'true',
    ) &&
    isWorkbookFeatureAvailable(getStore().getState(), 'workbook-map-data-export')
  );
}
